import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { BellOffIcon, InfoIcon, Loader2Icon, SaveIcon } from 'lucide-react'
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning'
import { Button } from '@/components/ui/button'
import { DataListToolbar } from '@/components/data/DataListToolbar'
import {
  getNotificationPreferencesApi,
  updateNotificationPreferencesApi,
  type NotificationPreference,
} from '@/lib/api'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// ── Icons per notification type ───────────────────────────────────────────

function typeIcon(key: string) {
  switch (key) {
    case 'cycle_due':
      return '📅'
    case 'cycle_overdue':
      return '⚠️'
    case 'payment_confirmed':
      return '✅'
    case 'circle_full':
      return '🎯'
    case 'participant_joined':
      return '👋'
    case 'payout_received':
      return '💰'
    case 'verification_update':
      return '🛡️'
    default:
      return '🔔'
  }
}

// ── Route ──────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/dashboard/notification-preferences')({
  component: () => {
    const [preferences, setPreferences] = useState<NotificationPreference[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [fetchError, setFetchError] = useState<string | null>(null)
    const [saveError, setSaveError] = useState<string | null>(null)
    const [saveSuccess, setSaveSuccess] = useState(false)
    const [dirty, setDirty] = useState(false)
    useUnsavedChangesWarning(dirty)
    const initialLoaded = preferences.length > 0 || fetchError !== null
    const isInitialLoad = loading && !initialLoaded

    // ── Fetch ──────────────────────────────────────────────────────────────
    const fetchData = useCallback(async (signal?: { cancelled: boolean }) => {
      if (signal?.cancelled) return
      setLoading(true)
      setFetchError(null)
      try {
        const data = await getNotificationPreferencesApi()
        if (signal?.cancelled) return
        setPreferences(data.preferences)
      } catch (err) {
        if (signal?.cancelled) return
        setFetchError(err instanceof Error ? err.message : 'Failed to load preferences')
      } finally {
        if (!signal?.cancelled) setLoading(false)
      }
    }, [])

    useEffect(() => {
      const signal = { cancelled: false }
      fetchData(signal)
      return () => {
        signal.cancelled = true
      }
    }, [fetchData])

    // ── Toggle handler ─────────────────────────────────────────────────────
    const handleToggle = (type: string, channel: 'in_app' | 'push') => {
      setDirty(true)
      setSaveSuccess(false)
      setSaveError(null)
      setPreferences((prev) =>
        prev.map((p) =>
          p.notification_type === type
            ? { ...p, [channel]: !p[channel] }
            : p,
        ),
      )
    }

    // ── Save ───────────────────────────────────────────────────────────────
    const handleSave = async () => {
      setSaving(true)
      setSaveError(null)
      setSaveSuccess(false)
      try {
        const changed = preferences.map((p) => ({
          notification_type: p.notification_type,
          in_app: p.in_app,
          push: p.push,
        }))
        const res = await updateNotificationPreferencesApi(changed)
        setPreferences(res.preferences)
        setSaveSuccess(true)
        setDirty(false)
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Failed to save preferences')
      } finally {
        setSaving(false)
      }
    }

    // ── Subtitle ───────────────────────────────────────────────────────────
    const subtitle = isInitialLoad ? (
      <span className="inline-block h-4 w-56 animate-pulse rounded bg-muted align-middle" />
    ) : fetchError ? (
      <>Could not load preferences</>
    ) : (
      <>
        <span className="font-medium text-foreground">{preferences.length}</span>{' '}
        notification types · Control which notifications you receive and how
      </>
    )

    // ── Save status ────────────────────────────────────────────────────────
    const saveStatus =
      saveSuccess && !dirty ? (
        <span className="text-xs text-[var(--brand-mint)]">Preferences saved</span>
      ) : saveError ? (
        <span className="text-xs text-destructive">{saveError}</span>
      ) : null

    // ── Render ─────────────────────────────────────────────────────────────
    return (
      <div className="flex flex-col gap-6">
        <DataListToolbar
          title="Notification preferences"
          subtitle={subtitle}
          onRefresh={() => fetchData()}
          refreshing={loading && initialLoaded}
          loading={loading}
        />

        {fetchError && !loading ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card px-6 py-12 text-center">
            <BellOffIcon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">Couldn't load preferences</p>
            <p className="text-sm text-muted-foreground">{fetchError}</p>
            <Button variant="outline" size="sm" onClick={() => fetchData()}>
              Try again
            </Button>
          </div>
        ) : loading && !initialLoaded ? (
          <div className="flex items-center justify-center py-16">
            <Loader2Icon className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <TooltipProvider delayDuration={300}>
            {/* ── Instruction banner ── */}
            <div className="rounded-xl border border-border bg-card px-5 py-3 text-sm text-muted-foreground">
              Choose which notification types you receive in-app (via the bell icon) and as push
              notifications on your mobile device.
            </div>

            {/* ── Preferences list ── */}
            <div className="space-y-2">
              {preferences.map((pref) => (
                <div
                  key={pref.notification_type}
                  className={cn(
                    'flex flex-col gap-3 rounded-xl border p-4 transition-all sm:flex-row sm:items-center sm:justify-between',
                    'border-border bg-card hover:bg-muted/20',
                  )}
                >
                  {/* Label + description */}
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <span className="mt-0.5 text-lg leading-none shrink-0">{typeIcon(pref.notification_type)}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{pref.label}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">{pref.description}</p>
                    </div>
                  </div>

                  {/* Toggle switches */}
                  <div className="flex shrink-0 items-center gap-4">
                    {/* In-app toggle */}
                    <div className="flex items-center gap-1.5">
                      <label className="flex cursor-pointer items-center gap-2">
                        <span className="text-xs text-muted-foreground">In-app</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={pref.in_app}
                          aria-label={`${pref.label} in-app notifications`}
                          onClick={() => handleToggle(pref.notification_type, 'in_app')}
                          className={cn(
                            'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors',
                            pref.in_app ? 'bg-[var(--brand-mint)]' : 'bg-muted',
                          )}
                        >
                          <span
                            className={cn(
                              'inline-block size-3.5 rounded-full bg-white shadow-sm transition-transform',
                              pref.in_app ? 'translate-x-[18px]' : 'translate-x-[3px]',
                            )}
                          />
                        </button>
                      </label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-full text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                            aria-label="What does In-app mean?"
                          >
                            <InfoIcon className="size-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-56">
                          Shown in the notification bell on the web dashboard when you're logged into the employer portal.
                        </TooltipContent>
                      </Tooltip>
                    </div>

                    {/* Push toggle */}
                    <div className="flex items-center gap-1.5">
                      <label className="flex cursor-pointer items-center gap-2">
                        <span className="text-xs text-muted-foreground">Push</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={pref.push}
                          aria-label={`${pref.label} push notifications`}
                          onClick={() => handleToggle(pref.notification_type, 'push')}
                          className={cn(
                            'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors',
                            pref.push ? 'bg-[var(--brand-mint)]' : 'bg-muted',
                          )}
                        >
                          <span
                            className={cn(
                              'inline-block size-3.5 rounded-full bg-white shadow-sm transition-transform',
                              pref.push ? 'translate-x-[18px]' : 'translate-x-[3px]',
                            )}
                          />
                        </button>
                      </label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-full text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                            aria-label="What does Push mean?"
                          >
                            <InfoIcon className="size-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-56">
                          Sent to your mobile device as a push notification, even when you're not actively using the app.
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Save bar ── */}
            <div className="flex items-center justify-between rounded-xl border border-border bg-card px-5 py-3">
              <div className="flex items-center gap-2">
                {saveStatus}
                {dirty && !saveSuccess && (
                  <span className="text-xs text-muted-foreground">
                    You have unsaved changes
                  </span>
                )}
              </div>
              <Button
                type="button"
                size="sm"
                className="gap-1.5"
                onClick={handleSave}
                disabled={saving || !dirty}
              >
                {saving ? (
                  <Loader2Icon className="size-3.5 animate-spin" />
                ) : (
                  <SaveIcon className="size-3.5" />
                )}
                {saving ? 'Saving…' : 'Save preferences'}
              </Button>
            </div>
          </TooltipProvider>
        )}
      </div>
    )
  },
})
